import { ContextRelation, EntityRef } from './warning.model';

const RELATIONS: readonly ContextRelation[] = ['CULPRIT', 'REGULATOR', 'LANDMARK'];

const ENTITY_TYPE = /^[A-Z][A-Z0-9_]*$/;

export interface RelationPlaceholder {
  relation: ContextRelation;
  plural: boolean;
  entityType?: string;
}

function isRelation(value: string): value is ContextRelation {
  return (RELATIONS as readonly string[]).includes(value);
}

/**
 * Parses `@RELATION[S][:ENTITY_TYPE]`.
 *
 * An exact relation name is matched before the plural form, which is what keeps the grammar
 * unambiguous for any relation vocabulary — a relation whose name ends in `S` still reads as
 * itself rather than as the plural of a shorter one.
 *
 * The entity type is matched literally against `EntityRef.entityType` downstream, so a type
 * DataLayer adds needs no change here.
 */
export function parseRelationPlaceholder(name: string): RelationPlaceholder | undefined {
  if (!name.startsWith('@')) return undefined;
  const parts = name.slice(1).split(':');
  if (parts.length > 2) return undefined;
  const [head, entityType] = parts;
  if (entityType !== undefined && !ENTITY_TYPE.test(entityType)) return undefined;

  if (isRelation(head)) return { relation: head, plural: false, entityType };

  const singular = head.endsWith('S') ? head.slice(0, -1) : undefined;
  if (singular !== undefined && isRelation(singular)) {
    return { relation: singular, plural: true, entityType };
  }
  return undefined;
}

/** Collapses by the `(entityType, uid)` key `distinctRefs` uses, preserving context order. */
function distinct(refs: EntityRef[]): EntityRef[] {
  const seen = new Set<string>();
  return refs.filter((ref) => {
    const key = `${ref.entityType}:${ref.uid}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * The entities one `@` placeholder renders: every distinct match for a plural, only the
 * first for a singular, none for a name that is not a relation placeholder.
 */
export function relationRefs(refs: EntityRef[], name: string): EntityRef[] {
  const parsed = parseRelationPlaceholder(name);
  if (!parsed) return [];

  const matched = distinct(
    refs.filter(
      (ref) =>
        ref.relation === parsed.relation &&
        (parsed.entityType === undefined || ref.entityType === parsed.entityType)
    )
  );
  return parsed.plural ? matched : matched.slice(0, 1);
}

/**
 * Binds the `@` placeholders a template names to the entities its occurrence references.
 *
 * A placeholder resolving to no entity is left **unbound** rather than bound to `""`. An
 * absent param leaves ngx-translate's literal `{{…}}` in the output, which `interpolateStrict`
 * already fails the rung on; `""` would render "In , the job is late". An *unnamed* entity is
 * still an entity and does bind.
 *
 * `labelFor` is passed in rather than resolved here so this stays free of i18n and of the
 * dataspace's display names.
 */
export function relationParams(
  refs: EntityRef[],
  placeholders: Iterable<string>,
  labelFor: (ref: EntityRef) => string,
  locale: string
): Record<string, string> {
  const params: Record<string, string> = {};
  const list = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });

  for (const name of placeholders) {
    const matched = relationRefs(refs, name);
    if (matched.length === 0) continue;

    // A singular matches one entity, which the list format renders as its bare label.
    params[name] = list.format(matched.map(labelFor));
  }
  return params;
}
